import React, { useEffect, useRef, useState } from 'react';
import { FaLinux, FaWindows, FaApple } from 'react-icons/fa';
import { LuUpload, LuFileArchive, LuX } from 'react-icons/lu';
import { Button } from '../ui';

const OS_META = {
  1: { label: 'Linux', icon: FaLinux, packageHint: '.deb package', accept: '.deb' },
  2: { label: 'Windows', icon: FaWindows, packageHint: '.exe / .msi', accept: '.exe,.msi' },
  3: { label: 'Mac OS', icon: FaApple, packageHint: '.dmg / .pkg', accept: '.dmg,.pkg' },
};

/**
 * Modal for uploading a new agent package version.
 * Styled to match RT2 dialogs and the Agents settings page.
 */
const UploadAgentModal = ({ isOpen, onClose, onUpload, isLoading, selectedOS }) => {
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState('');
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const os = OS_META[selectedOS] || OS_META[1];
  const OsIcon = os.icon;

  const resetForm = () => {
    setFile(null);
    setVersion('');
    setError('');
    setIsDragging(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (isLoading) return;
    resetForm();
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        if (!isLoading) {
          resetForm();
          onClose();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, isLoading, onClose]);

  const applyFile = (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    setError('');

    // Soft-fill version from filename like robitest-agent_1.2.0.deb
    const match = selectedFile.name.match(/(\d+\.\d+(?:\.\d+)?)/);
    if (match && !version.trim()) {
      setVersion(match[1]);
    }
  };

  const handleFileChange = (e) => {
    applyFile(e.target.files?.[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoading) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (isLoading) return;
    applyFile(e.dataTransfer.files?.[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!file) {
      setError('Please select an agent package file');
      return;
    }

    if (!version.trim()) {
      setError('Please enter a version (for example 1.2.0)');
      return;
    }

    try {
      await onUpload(file, selectedOS, version.trim());
      resetForm();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.detail || 'Upload failed. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="rt-dialog-overlay agents-upload-overlay"
      data-rt-modal-overlay
      onClick={handleClose}
      role="presentation"
    >
      <div
        className="rt-dialog agents-upload-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="agents-upload-title"
      >
        <header className="agents-upload-dialog__header">
          <div className="agents-upload-dialog__brand">
            <span className="agents-upload-dialog__icon" aria-hidden>
              <OsIcon size={18} />
            </span>
            <div>
              <h3 className="agents-upload-dialog__title" id="agents-upload-title">
                Upload Agent
              </h3>
              <p className="agents-upload-dialog__subtitle">
                Publish a new <strong>{os.label}</strong> release ({os.packageHint})
              </p>
            </div>
          </div>
          <button
            type="button"
            className="agents-upload-dialog__close"
            onClick={handleClose}
            disabled={isLoading}
            aria-label="Close"
            data-rt-modal-close
          >
            <LuX size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="agents-upload-dialog__form">
          <div className="agents-upload-field">
            <label className="agents-upload-field__label" htmlFor="agent-file-input">
              Agent package
            </label>
            <input
              ref={fileInputRef}
              id="agent-file-input"
              type="file"
              accept={os.accept}
              disabled={isLoading}
              className="agents-upload-file-input"
              onChange={handleFileChange}
            />
            <label
              htmlFor="agent-file-input"
              className={`agents-upload-dropzone${isDragging ? ' agents-upload-dropzone--dragging' : ''}${
                file ? ' agents-upload-dropzone--filled' : ''
              }${isLoading ? ' agents-upload-dropzone--disabled' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <span className="agents-upload-dropzone__icon">
                {file ? <LuFileArchive size={22} /> : <LuUpload size={22} />}
              </span>
              {file ? (
                <>
                  <span className="agents-upload-dropzone__title">{file.name}</span>
                  <span className="agents-upload-dropzone__hint">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB · click or drop to replace
                  </span>
                </>
              ) : (
                <>
                  <span className="agents-upload-dropzone__title">
                    Drop {os.packageHint} here, or click to browse
                  </span>
                  <span className="agents-upload-dropzone__hint">
                    Only one package per upload · version is required below
                  </span>
                </>
              )}
            </label>
          </div>

          <div className="agents-upload-field">
            <label className="agents-upload-field__label" htmlFor="agent-version-input">
              Version
            </label>
            <input
              id="agent-version-input"
              type="text"
              value={version}
              onChange={(e) => {
                setVersion(e.target.value);
                setError('');
              }}
              placeholder="e.g. 1.2.0"
              disabled={isLoading}
              className="agents-upload-input"
              autoComplete="off"
            />
            <p className="agents-upload-field__hint">
              Use a unique version for this OS. Duplicates are rejected by the API.
            </p>
          </div>

          {error && (
            <div className="agents-upload-error" role="alert">
              {error}
            </div>
          )}

          <div className="agents-upload-dialog__actions">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isLoading}
              icon={!isLoading ? <LuUpload size={14} /> : undefined}
              disabled={isLoading}
            >
              {isLoading ? 'Uploading…' : 'Upload package'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadAgentModal;
